import React, { useEffect, useState } from "react";
import { Upload, Trash2, Link2, Image as ImageIcon, Loader2, Plus, X } from "lucide-react";
import { getPhotos, uploadPhoto, deletePhoto, getPropertyLinks, linkVendorProperty, deletePropertyLink, fetchProperties } from "../../api";
import { useToast } from "../../hooks/use-toast";

const AdminPhotoLibrary = () => {
  const { toast } = useToast();
  const [photos, setPhotos] = useState([]);
  const [links, setLinks] = useState([]);
  const [props, setProps] = useState([]);
  const [busy, setBusy] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ propertyTitle: "", vendorEmail: "", caption: "" });
  const [linkForm, setLinkForm] = useState({ userEmail: "", propertyId: "", role: "vendor" });

  const load = () => {
    Promise.all([getPhotos(), getPropertyLinks(), fetchProperties()])
      .then(([p, l, pr]) => { setPhotos(p); setLinks(l); setProps(Array.isArray(pr) ? pr : (pr.items || pr.properties || [])); })
      .catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(load, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) await uploadPhoto(f, meta);
      toast({ title: `${files.length} photo(s) added` });
      setMeta({ propertyTitle: "", vendorEmail: "", caption: "" });
      load();
    } catch { toast({ title: "Upload failed" }); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => { await deletePhoto(id); setPhotos((ps) => ps.filter((p) => p.id !== id)); };

  const addLink = async (e) => {
    e.preventDefault();
    if (!linkForm.userEmail || !linkForm.propertyId) return;
    try { await linkVendorProperty(linkForm); toast({ title: "Linked" }); setLinkForm({ userEmail: "", propertyId: "", role: "vendor" }); load(); }
    catch (err) { toast({ title: "Link failed", description: err?.response?.data?.detail || "" }); }
  };
  const removeLink = async (id) => { await deletePropertyLink(id); setLinks((ls) => ls.filter((l) => l.id !== id)); };

  if (busy) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading library…</div>;
  const inp = "h-10 px-3 rounded-lg border border-[#e2d8c8] text-[13px] outline-none focus:border-[#b8935f]";

  return (
    <div data-testid="admin-photo-library" className="space-y-8">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><ImageIcon size={17} className="text-[#b8935f]" /> Photography library</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Upload past property photography and link it to the property & vendor/seller.</p>
        <div className="grid sm:grid-cols-3 gap-2 mb-3">
          <input placeholder="Property title" value={meta.propertyTitle} onChange={(e) => setMeta({ ...meta, propertyTitle: e.target.value })} className={inp} data-testid="photo-property" />
          <input placeholder="Vendor / seller email" value={meta.vendorEmail} onChange={(e) => setMeta({ ...meta, vendorEmail: e.target.value })} className={inp} data-testid="photo-vendor" />
          <input placeholder="Caption (optional)" value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} className={inp} />
        </div>
        <label className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#1c1c1e] text-white text-[14px] font-500 cursor-pointer hover:bg-black transition-colors" data-testid="photo-upload-btn">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload photos
          <input type="file" accept="image/*" multiple onChange={onUpload} className="hidden" disabled={uploading} />
        </label>
      </div>

      <div>
        <p className="text-[13px] text-[#6a6a6a] mb-3">{photos.length} photo(s) in library</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
          {photos.map((p) => (
            <div key={p.id} className="group relative rounded-xl overflow-hidden border border-[#f0ece5] bg-white">
              <img src={p.url} alt={p.caption} className="w-full h-36 object-cover" />
              <button onClick={() => remove(p.id)} data-testid="photo-delete" className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
              <div className="p-2.5"><p className="text-[12px] font-500 text-[#2b2b2b] truncate">{p.propertyTitle || p.filename}</p>{p.vendorEmail && <p className="text-[11px] text-[#a0a0a0] truncate">{p.vendorEmail}</p>}</div>
            </div>
          ))}
          {photos.length === 0 && <p className="text-[13px] text-[#c0c0c0] col-span-full py-8 text-center">No photos yet — upload your first above.</p>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Link2 size={17} className="text-[#b8935f]" /> Vendor / seller ↔ property links</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Keep every vendor & seller connected to their property in one place.</p>
        <form onSubmit={addLink} className="grid sm:grid-cols-4 gap-2 mb-4">
          <input required placeholder="Vendor / seller email" value={linkForm.userEmail} onChange={(e) => setLinkForm({ ...linkForm, userEmail: e.target.value })} className={inp} data-testid="link-email" />
          <select value={linkForm.propertyId} onChange={(e) => setLinkForm({ ...linkForm, propertyId: e.target.value })} className={inp} data-testid="link-property">
            <option value="">Select property…</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select value={linkForm.role} onChange={(e) => setLinkForm({ ...linkForm, role: e.target.value })} className={inp}>
            <option value="vendor">Vendor</option><option value="seller">Seller</option><option value="landlord">Landlord</option>
          </select>
          <button type="submit" className="h-10 rounded-lg bg-[#b8935f] text-white text-[13px] font-500 inline-flex items-center justify-center gap-1.5 hover:bg-[#a67f4d]" data-testid="link-submit"><Plus size={14} /> Link</button>
        </form>
        <div className="space-y-2" data-testid="property-links">
          {links.map((l) => (
            <div key={l.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
              {l.propertyImage && <img src={l.propertyImage} alt="" className="w-10 h-10 rounded object-cover" />}
              <div className="min-w-0 flex-1"><p className="text-[13px] font-500 text-[#2b2b2b] truncate">{l.userName} <span className="text-[11px] text-[#b8935f] capitalize">· {l.role}</span></p><p className="text-[12px] text-[#a0a0a0] truncate">{l.propertyTitle}</p></div>
              <button onClick={() => removeLink(l.id)} className="text-[#c0b8a8] hover:text-red-500"><X size={16} /></button>
            </div>
          ))}
          {links.length === 0 && <p className="text-[13px] text-[#c0c0c0]">No links yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default AdminPhotoLibrary;
